import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { Debounce } from '@core/decorators/debounce.decorator';
import { APP_ROUTES } from '@core/routes';
import { TranslationService } from '@core/translation.service';
import { CustomerChatService } from '@features/customer/chat/customer-chat.service';
import { CustomerAvatarStatusComponent } from '@features/customer/components/customer-avatar-status/customer-avatar-status.component';
import { CustomerShortInfoCardComponent } from '@features/customer/components/customer-short-card/customer-short-info-card.component';
import { Customer } from '@features/customer/models/customer';
import { CustomerService } from '@features/customer/services/customer.service';
import { activeCustomer, activeCustomerId } from '@features/customer/store/work-with-customer.signals';
import { WorkWithCustomerStore } from '@features/customer/store/work-with-customer.store';
import { UserShortCardComponent } from '@features/user/components/user-short-card/user-short-card.component';
import { TranslateModule } from '@ngx-translate/core';
import { PnycDropdownDirective } from '@ui-kit/directives/pnyc-dropdown/pnyc-dropdown.directive';
import { PnycAvatarComponent } from '@ui-kit/pnyc-avatar/pnyc-avatar.component';
import { PnycButtonComponent } from '@ui-kit/pnyc-button/pnyc-button.component';
import { PnycIconComponent } from '@ui-kit/pnyc-icon/pnyc-icon.component';
import { PnycInputComponent } from '@ui-kit/pnyc-input/pnyc-input.component';
import { PnycListItemComponent } from '@ui-kit/pnyc-list-item/pnyc-list-item.component';
import { locale } from './i18n/en';

@Component({
    selector: 'customer-dropdown',
    imports: [
        NgTemplateOutlet,
        PnycDropdownDirective,
        PnycIconComponent,
        PnycButtonComponent,
        PnycInputComponent,
        PnycAvatarComponent,
        PnycListItemComponent,
        UserShortCardComponent,
        CustomerAvatarStatusComponent,
        CustomerShortInfoCardComponent,
        TranslateModule
    ],
    templateUrl: './customer-dropdown.component.html',
    styleUrl: './customer-dropdown.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerDropdownComponent {
    public readonly activeCustomerId = activeCustomerId;
    public readonly activeCustomer = activeCustomer;

    private readonly router = inject(Router);
    private readonly customerService = inject(CustomerService);
    private readonly workWithCustomerStore = inject(WorkWithCustomerStore);
    private readonly customerChatService = inject(CustomerChatService);
    private readonly translationLoaderService = inject(TranslationService);

    public searchPattern = signal('');
    public searchValue = signal('');

    public customersQuery = this.customerService.searchCustomers();

    public customersList = computed(() => this.customersQuery.value()?.filter(o => !o.isHotProspect) ?? []);
    public hotProspectsList = computed(() => this.customersQuery.value()?.filter(o => o.isHotProspect) ?? []);

    private readonly searchEffect = effect(() => {
        const pattern = this.searchPattern();

        untracked(() => {
            this.customersQuery.fetch({ filter: 'ALL', searchPattern: pattern });
        });
    });

    constructor() {
        this.translationLoaderService.loadTranslations(locale);
    }

    public onSearchChanged(value = ''): void {
        this.searchValue.set(value);
        this.debouncedSearchUpdate(value);
    }

    public setCustomer(customerId: number): void {
        if (this.activeCustomerId() === customerId) {
            this.workWithCustomerStore.clearActiveCustomer();

            return;
        }

        const customer = this.customersQuery.value()?.find((c: Customer) => c.id === customerId);

        if (customer) {
            this.workWithCustomerStore.setActiveCustomer(customer);
        }
    }

    public clearCustomer(): void {
        this.workWithCustomerStore.clearActiveCustomer();
    }

    public openCustomerProfile(customerId: number): void {
        this.router.navigate([APP_ROUTES.CUSTOMER, customerId]);
    }

    public onViewCustomersClicked(): void {
        this.router.navigate([APP_ROUTES.CUSTOMERS]);
    }

    public onDropdownClosed(): void {
        this.searchPattern.set('');
        this.searchValue.set('');
    }

    public addCustomerClicked(): void {
        this.customerChatService.openCustomerCreationInChat();
    }

    @Debounce(200)
    private debouncedSearchUpdate(value: string): void {
        this.searchPattern.set(value);
    }
}
